import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowLeft, HelpCircle } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * 时间戳工具帮助文档页面
 */
function TimestampHelp() {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);

  // 监听滚动，高亮当前章节
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id^="section-"]');
      let currentSection = 0;

      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 100 && rect.bottom > 100) {
          currentSection = index;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初始调用

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const content = {
    zh: {
      title: '时间戳转换工具使用指南',
      subtitle: '快速掌握DevToolkit的时间戳工具',
      sections: [
        {
          title: '功能特性',
          items: [
            {
              title: '双向转换',
              desc: '支持时间戳转日期时间，也支持日期时间转时间戳，完全双向操作。'
            },
            {
              title: '多时区支持',
              desc: '自动识别本地时区，也可以手动选择其他时区进行转换，满足全球化需求。'
            },
            {
              title: '多种格式',
              desc: '支持秒级时间戳（10位）和毫秒级时间戳（13位），自动识别格式。'
            },
            {
              title: '实时显示',
              desc: '实时显示当前时间戳，方便快速获取当前时间的Unix时间戳。'
            },
            {
              title: '批量转换',
              desc: '支持批量输入多个时间戳进行转换，提高工作效率。'
            },
            {
              title: '格式化选项',
              desc: '多种日期时间格式可选，包括ISO 8601、本地化格式等。'
            }
          ]
        },
        {
          title: '使用教程',
          content: `
            <h3>1. 时间戳转日期</h3>
            <p><strong>步骤 1：输入时间戳</strong></p>
            <p>在输入框中粘贴或输入时间戳。支持10位（秒级）或13位（毫秒级）时间戳。</p>

            <p><strong>步骤 2：自动转换</strong></p>
            <p>输入后工具会自动识别时间戳格式并转换为可读的日期时间格式。</p>

            <p><strong>步骤 3：选择时区（可选）</strong></p>
            <p>默认使用本地时区，也可以选择其他时区查看对应时区的时间。</p>

            <h3>2. 日期转时间戳</h3>
            <p><strong>步骤 1：选择日期时间</strong></p>
            <p>使用日期时间选择器选择需要转换的日期和时间。</p>

            <p><strong>步骤 2：选择时间戳格式</strong></p>
            <ul>
              <li><strong>秒级时间戳：</strong>10位数字，精确到秒</li>
              <li><strong>毫秒级时间戳：</strong>13位数字，精确到毫秒</li>
            </ul>

            <p><strong>步骤 3：复制结果</strong></p>
            <p>点击复制按钮快速复制转换后的时间戳。</p>

            <h3>3. 获取当前时间戳</h3>
            <p><strong>实时显示：</strong></p>
            <p>页面顶部会实时显示当前的Unix时间戳（秒级和毫秒级），点击即可复制。</p>

            <p><strong>应用场景：</strong></p>
            <ul>
              <li>需要记录当前时间点</li>
              <li>生成唯一标识符</li>
              <li>测试时间相关功能</li>
            </ul>

            <h3>4. 批量转换</h3>
            <p><strong>输入多个时间戳：</strong></p>
            <p>在文本框中每行输入一个时间戳，工具会批量转换所有时间戳。</p>

            <p><strong>导出结果：</strong></p>
            <p>转换结果可以批量复制或导出为CSV格式，方便在Excel中使用。</p>

            <h3>5. 时区转换</h3>
            <p><strong>查看不同时区时间：</strong></p>
            <p>同一个时间戳在不同时区显示的日期时间不同，可以：</p>
            <ul>
              <li>选择目标时区</li>
              <li>查看对应的本地时间</li>
              <li>对比多个时区的时间差异</li>
            </ul>
          `
        },
        {
          title: '常见问题',
          faqs: [
            {
              q: '什么是Unix时间戳？',
              a: 'Unix时间戳是从1970年1月1日00:00:00 UTC到现在的秒数（或毫秒数）。它是一种与时区无关的时间表示方式，广泛用于计算机系统中。'
            },
            {
              q: '10位和13位时间戳有什么区别？',
              a: '10位时间戳是秒级精度，例如1609459200表示2021-01-01 00:00:00。13位时间戳是毫秒级精度，例如1609459200000。毫秒级时间戳更精确，常用于需要高精度时间的场景。'
            },
            {
              q: '如何判断时间戳的单位？',
              a: '通常10位数字是秒级时间戳，13位数字是毫秒级时间戳。本工具会自动识别。如果转换结果的年份看起来不对（比如显示1970年或2286年），说明单位识别错误，可以手动切换。'
            },
            {
              q: '时间戳转换结果不对怎么办？',
              a: '首先确认时间戳位数是否正确（10位或13位）。其次检查时区设置是否正确。如果时间戳来自其他系统，确认该系统使用的是Unix时间戳而不是其他时间格式。'
            },
            {
              q: '为什么同一时间戳在不同时区显示不同？',
              a: '时间戳本身是UTC时间，转换为本地时间时需要根据时区进行调整。例如，同一时间戳在北京（UTC+8）显示为上午8点，在纽约（UTC-5）显示为前一天晚上7点。'
            },
            {
              q: '支持未来时间吗？',
              a: '支持。时间戳可以表示任何时间，包括过去、现在和未来。只要是有效的数字，工具都可以转换。'
            },
            {
              q: '如何处理时区缩写（如CST、EST）？',
              a: 'CST等缩写可能有歧义（中国标准时间和美国中部时间都是CST）。建议使用完整的时区名称（如Asia/Shanghai、America/New_York）或UTC偏移（如UTC+8、UTC-5）。'
            },
            {
              q: '转换会丢失精度吗？',
              a: '不会。工具完全保留时间戳的精度。秒级时间戳转换精确到秒，毫秒级时间戳转换精确到毫秒。'
            },
            {
              q: '能转换负数时间戳吗？',
              a: '可以。负数时间戳表示1970年1月1日之前的时间。例如-86400表示1969年12月31日。'
            },
            {
              q: '如何在代码中使用时间戳？',
              a: 'JavaScript中使用Date.now()获取当前毫秒级时间戳，Math.floor(Date.now()/1000)获取秒级时间戳。Python中使用time.time()获取秒级时间戳。'
            }
          ]
        },
        {
          title: '使用场景',
          content: `
            <h3>1. API开发和调试</h3>
            <ul>
              <li>查看API返回的时间戳含义</li>
              <li>生成测试用的时间戳数据</li>
              <li>调试时间相关的逻辑错误</li>
              <li>验证时区转换是否正确</li>
            </ul>

            <h3>2. 日志分析</h3>
            <ul>
              <li>将日志中的时间戳转换为可读格式</li>
              <li>分析不同时区的日志文件</li>
              <li>定位特定时间点的日志</li>
            </ul>

            <h3>3. 数据库操作</h3>
            <ul>
              <li>查询数据库中时间戳字段的含义</li>
              <li>生成插入数据库的时间戳值</li>
              <li>对比不同记录的时间差</li>
            </ul>

            <h3>4. 跨时区协作</h3>
            <ul>
              <li>查看其他时区的会议时间</li>
              <li>转换国际项目的截止时间</li>
              <li>协调全球团队的工作时间</li>
            </ul>

            <h3>5. 数据迁移</h3>
            <ul>
              <li>批量转换历史数据的时间戳</li>
              <li>统一不同系统的时间格式</li>
              <li>验证迁移后的时间数据</li>
            </ul>
          `
        },
        {
          title: '时区参考',
          content: `
            <h3>常用时区</h3>
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">时区</th>
                  <th class="text-left">UTC偏移</th>
                  <th class="text-left">主要城市</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>中国标准时间 (CST)</td>
                  <td>UTC+8</td>
                  <td>北京、上海、香港</td>
                </tr>
                <tr>
                  <td>日本标准时间 (JST)</td>
                  <td>UTC+9</td>
                  <td>东京、大阪</td>
                </tr>
                <tr>
                  <td>美国东部时间 (EST/EDT)</td>
                  <td>UTC-5/-4</td>
                  <td>纽约、华盛顿</td>
                </tr>
                <tr>
                  <td>美国西部时间 (PST/PDT)</td>
                  <td>UTC-8/-7</td>
                  <td>洛杉矶、旧金山</td>
                </tr>
                <tr>
                  <td>英国时间 (GMT/BST)</td>
                  <td>UTC+0/+1</td>
                  <td>伦敦</td>
                </tr>
                <tr>
                  <td>欧洲中部时间 (CET/CEST)</td>
                  <td>UTC+1/+2</td>
                  <td>巴黎、柏林</td>
                </tr>
              </tbody>
            </table>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-4">
              注：带斜杠的时区表示标准时间/夏令时。夏令时通常在3-11月实施。
            </p>
          `
        }
      ]
    },
    en: {
      title: 'Timestamp Converter Tool Guide',
      subtitle: 'Quickly master DevToolkit\'s timestamp tool',
      sections: [
        {
          title: 'Features',
          items: [
            {
              title: 'Bidirectional Conversion',
              desc: 'Convert timestamp to datetime and datetime to timestamp, fully bidirectional.'
            },
            {
              title: 'Multi-timezone Support',
              desc: 'Auto-detects local timezone, also allows manual timezone selection for global needs.'
            },
            {
              title: 'Multiple Formats',
              desc: 'Supports second-level (10-digit) and millisecond-level (13-digit) timestamps with auto-detection.'
            },
            {
              title: 'Real-time Display',
              desc: 'Real-time display of current timestamp for quick access to current Unix timestamp.'
            },
            {
              title: 'Batch Conversion',
              desc: 'Support batch input of multiple timestamps for conversion, improving efficiency.'
            },
            {
              title: 'Format Options',
              desc: 'Multiple datetime format options including ISO 8601, localized formats, etc.'
            }
          ]
        },
        {
          title: 'Tutorial',
          content: `
            <h3>1. Timestamp to Date</h3>
            <p><strong>Step 1: Input Timestamp</strong></p>
            <p>Paste or type timestamp in the input box. Supports 10-digit (second) or 13-digit (millisecond) timestamps.</p>

            <p><strong>Step 2: Auto Convert</strong></p>
            <p>After input, tool auto-detects timestamp format and converts to readable datetime format.</p>

            <p><strong>Step 3: Select Timezone (Optional)</strong></p>
            <p>Defaults to local timezone, can also select other timezones to view corresponding times.</p>

            <h3>2. Date to Timestamp</h3>
            <p><strong>Step 1: Select Datetime</strong></p>
            <p>Use datetime picker to select the date and time to convert.</p>

            <p><strong>Step 2: Choose Timestamp Format</strong></p>
            <ul>
              <li><strong>Second-level:</strong> 10 digits, accurate to seconds</li>
              <li><strong>Millisecond-level:</strong> 13 digits, accurate to milliseconds</li>
            </ul>

            <p><strong>Step 3: Copy Result</strong></p>
            <p>Click copy button to quickly copy converted timestamp.</p>

            <h3>3. Get Current Timestamp</h3>
            <p><strong>Real-time Display:</strong></p>
            <p>Top of page shows real-time current Unix timestamp (second and millisecond level), click to copy.</p>

            <p><strong>Use Cases:</strong></p>
            <ul>
              <li>Need to record current time point</li>
              <li>Generate unique identifiers</li>
              <li>Test time-related functions</li>
            </ul>

            <h3>4. Batch Conversion</h3>
            <p><strong>Input Multiple Timestamps:</strong></p>
            <p>Enter one timestamp per line in text box, tool will batch convert all timestamps.</p>

            <p><strong>Export Results:</strong></p>
            <p>Conversion results can be batch copied or exported as CSV format for use in Excel.</p>

            <h3>5. Timezone Conversion</h3>
            <p><strong>View Different Timezone Times:</strong></p>
            <p>Same timestamp displays different datetime in different timezones, you can:</p>
            <ul>
              <li>Select target timezone</li>
              <li>View corresponding local time</li>
              <li>Compare time differences across multiple timezones</li>
            </ul>
          `
        },
        {
          title: 'FAQ',
          faqs: [
            {
              q: 'What is Unix timestamp?',
              a: 'Unix timestamp is the number of seconds (or milliseconds) from January 1, 1970 00:00:00 UTC to now. It\'s a timezone-independent time representation widely used in computer systems.'
            },
            {
              q: 'What\'s the difference between 10-digit and 13-digit timestamps?',
              a: '10-digit timestamp is second-level precision, e.g., 1609459200 represents 2021-01-01 00:00:00. 13-digit timestamp is millisecond-level precision, e.g., 1609459200000. Millisecond-level is more precise, commonly used in scenarios requiring high time precision.'
            },
            {
              q: 'How to determine timestamp unit?',
              a: 'Usually 10 digits is second-level, 13 digits is millisecond-level. This tool auto-detects. If conversion result year looks wrong (like showing 1970 or 2286), unit detection is incorrect, can manually switch.'
            },
            {
              q: 'What if timestamp conversion result is wrong?',
              a: 'First confirm timestamp digits are correct (10 or 13). Second check timezone settings are correct. If timestamp is from another system, confirm it uses Unix timestamp not other time formats.'
            },
            {
              q: 'Why same timestamp shows different in different timezones?',
              a: 'Timestamp itself is UTC time, converting to local time requires timezone adjustment. For example, same timestamp shows 8 AM in Beijing (UTC+8) and 7 PM previous day in New York (UTC-5).'
            },
            {
              q: 'Does it support future times?',
              a: 'Yes. Timestamps can represent any time including past, present, and future. As long as it\'s a valid number, the tool can convert it.'
            },
            {
              q: 'How to handle timezone abbreviations (like CST, EST)?',
              a: 'Abbreviations like CST can be ambiguous (China Standard Time and Central Standard Time are both CST). Recommend using full timezone names (like Asia/Shanghai, America/New_York) or UTC offsets (like UTC+8, UTC-5).'
            },
            {
              q: 'Will conversion lose precision?',
              a: 'No. Tool fully preserves timestamp precision. Second-level timestamp converts accurate to seconds, millisecond-level converts accurate to milliseconds.'
            },
            {
              q: 'Can negative timestamps be converted?',
              a: 'Yes. Negative timestamps represent times before January 1, 1970. For example, -86400 represents December 31, 1969.'
            },
            {
              q: 'How to use timestamps in code?',
              a: 'In JavaScript use Date.now() to get current millisecond-level timestamp, Math.floor(Date.now()/1000) for second-level. In Python use time.time() for second-level timestamp.'
            }
          ]
        },
        {
          title: 'Use Cases',
          content: `
            <h3>1. API Development and Debugging</h3>
            <ul>
              <li>View meaning of API returned timestamps</li>
              <li>Generate test timestamp data</li>
              <li>Debug time-related logic errors</li>
              <li>Verify timezone conversion correctness</li>
            </ul>

            <h3>2. Log Analysis</h3>
            <ul>
              <li>Convert timestamps in logs to readable format</li>
              <li>Analyze log files from different timezones</li>
              <li>Locate logs at specific time points</li>
            </ul>

            <h3>3. Database Operations</h3>
            <ul>
              <li>Query meaning of timestamp fields in database</li>
              <li>Generate timestamp values for database insertion</li>
              <li>Compare time differences between different records</li>
            </ul>

            <h3>4. Cross-timezone Collaboration</h3>
            <ul>
              <li>View meeting times in other timezones</li>
              <li>Convert deadlines for international projects</li>
              <li>Coordinate work times for global teams</li>
            </ul>

            <h3>5. Data Migration</h3>
            <ul>
              <li>Batch convert timestamps in historical data</li>
              <li>Unify time formats across different systems</li>
              <li>Verify time data after migration</li>
            </ul>
          `
        },
        {
          title: 'Timezone Reference',
          content: `
            <h3>Common Timezones</h3>
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">Timezone</th>
                  <th class="text-left">UTC Offset</th>
                  <th class="text-left">Major Cities</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>China Standard Time (CST)</td>
                  <td>UTC+8</td>
                  <td>Beijing, Shanghai, Hong Kong</td>
                </tr>
                <tr>
                  <td>Japan Standard Time (JST)</td>
                  <td>UTC+9</td>
                  <td>Tokyo, Osaka</td>
                </tr>
                <tr>
                  <td>US Eastern Time (EST/EDT)</td>
                  <td>UTC-5/-4</td>
                  <td>New York, Washington</td>
                </tr>
                <tr>
                  <td>US Pacific Time (PST/PDT)</td>
                  <td>UTC-8/-7</td>
                  <td>Los Angeles, San Francisco</td>
                </tr>
                <tr>
                  <td>UK Time (GMT/BST)</td>
                  <td>UTC+0/+1</td>
                  <td>London</td>
                </tr>
                <tr>
                  <td>Central European Time (CET/CEST)</td>
                  <td>UTC+1/+2</td>
                  <td>Paris, Berlin</td>
                </tr>
              </tbody>
            </table>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-4">
              Note: Timezones with slashes indicate standard time/daylight saving time. DST is usually implemented March-November.
            </p>
          `
        }
      ]
    }
  };

  const data = content[language];

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900">
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-8">
          {/* 主内容区 */}
          <article className="flex-1 min-w-0">
            {/* 页面标题 */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <Clock size={28} className="text-orange-600 dark:text-orange-400" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{data.title}</h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">{data.subtitle}</p>
            </div>

            <div className="prose prose-lg dark:prose-invert max-w-none
          prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700
          prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
          prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-orange-600 dark:prose-a:text-orange-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
          prose-ul:my-4 prose-li:my-2
          prose-table:border-collapse prose-table:w-full
          prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:p-3
          prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:p-3
          prose-code:text-orange-600 dark:prose-code:text-orange-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
          prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-pre:p-4 prose-pre:rounded-lg
        ">
          {data.sections.map((section, index) => (
            <section key={index} id={`section-${index}`}>
              <h2>
                {section.title}
              </h2>

              {section.items && (
                <div className="grid md:grid-cols-2 gap-6 not-prose">
                  {section.items.map((item, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{item.title}</h4>
                      <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {section.content && (
                <div dangerouslySetInnerHTML={{ __html: section.content }} />
              )}

              {section.faqs && (
                <div className="space-y-6">
                  {section.faqs.map((faq, i) => (
                    <div key={i} className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-6 border border-orange-200 dark:border-orange-700">
                      <div className="flex items-start gap-3">
                        <HelpCircle size={20} className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{faq.q}</h4>
                          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{faq.a}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-2xl p-8 border border-orange-200 dark:border-orange-700">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {language === 'zh' ? '开始使用' : 'Get Started'}
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {language === 'zh'
              ? '立即体验 DevToolkit 的时间戳转换工具，轻松处理各种时间格式！'
              : 'Experience DevToolkit\'s timestamp converter now for easy handling of various time formats!'}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/timestamp"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
            >
              <Clock size={20} />
              {language === 'zh' ? '打开时间戳工具' : 'Open Timestamp Tool'}
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 font-medium transition-colors"
            >
              {language === 'zh' ? '查看更多教程' : 'More Tutorials'}
            </Link>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/timestamp"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回时间戳工具' : 'Back to Timestamp Tool'}
          </Link>
        </div>
      </article>

      {/* 右侧大纲 */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-20">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              {language === 'zh' ? '目录' : 'Contents'}
            </h2>
            <nav className="space-y-1">
              {data.sections.map((section, index) => (
                <a
                  key={index}
                  href={`#section-${index}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`section-${index}`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeSection === index
                      ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`flex-shrink-0 w-5 text-xs ${activeSection === index ? 'font-bold' : ''}`}>
                      {index + 1}.
                    </span>
                    <span className="line-clamp-2">{section.title}</span>
                  </span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      </aside>
    </div>
      </div>
    </div>
  );
}

export default TimestampHelp;
